import * as React from 'react'
import { useState, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { Box } from '@mui/material'

import { UserContext } from '../UserContext'
import {
    useContextsStore,
    selectIsPublicView,
} from '../stores/useContextsStore'
import AppBar from '../components/AppBar'
import GraphMenu from '../components/GraphMenu'
import ImportDialog from '../components/Imports'
import { useZustand } from '../hooks/useZustand'
import usePageInfo from '../hooks/usePageInfo'
import { useGraphStore } from '../stores/useGraphStore'

import { DialogsContext } from '../DialogsContext'

import StatementFilterPanel from '../components/StatementFilterPanel'

import { useSearchParams } from 'react-router-dom'

import FilterEventBridge from '../components/StatementFilterPanel/FilterEventBridge'
import FirstEntryController from '../components/FirstEntry'
import LanguageWarnings from '../components/LanguageWarnings'
import AIChat from '../components/AIChat'
import PanelToggles from '../components/PanelToggles'
import { useSnackbar } from '../components/Snackbar'
import LockedGraphScreen from '../components/Paywall/LockedGraphScreen'
import {
    isHideAlways,
    isHideEdit,
    isGraphOnlyMode,
    isHideStatements,
    isHideAiActions,
} from '../components/LeftMenuMigration/legacyBridge'
import { usePanelStore } from '../stores/usePanelStore'
// if (typeof window !== 'undefined') {
//     import('@infranodus/infranodus-ai/css')
// }
import LazyGraph from './LazyGraph'
import HelmetCode from '../HelmetCode'
// SearchInput is mounted directly in the AppBar for the find view
// (replaces the page title + advanced-mode toggle). It's not rendered here
// inside <main>. SearchEmptyStates fills the graph slot when no graph.
import SearchEmptyStates from '../components/SearchInput/SearchEmptyStates'
import { useFindStore, SEARCH_PHASES } from '../stores/useFindStore'
import AccessGate from '../components/AccessGate'

const updatePanelOpenSelector = state => state.updatePanelOpen
const updateStatementsViewTypeSelector = state => state.updateStatementsViewType

const setForceRequestModeSelector = state => state.setForceRequestMode
const setAutoAddFromAiChatSelector = state => state.setAutoAddFromAiChat
const setAddUserMessagesToGraphSelector = state =>
    state.setAddUserMessagesToGraph

export default function Home() {
    const {
        activeImportDialog,
        setActiveImportDialog,
        setBackLinkDialog,
        closeImportDialog,
        presetSearchString,
        setPresetSearchString,
        presetContext,
        setPresetContext,
        presetFiles,
    } = React.useContext(DialogsContext)

    const [urlParams] = useSearchParams()

    const { location, label } = usePageInfo()
    const { username } = React.useContext(UserContext)
    const isPublic = useContextsStore(selectIsPublicView)
    const [isAppBarOpen, setIsAppBarOpen] = useState(false)
    // Embed/share URL flags reflect the original sharer's panel layout so
    // recipients land on the same arrangement:
    //   - `?hide_always=1` — both side panels closed on load (buttons stay).
    //   - `?hide_edit=1`    — statements list closed, AI panel left to its
    //                          own default. (Statements *control* row stays.)
    //   - `?hide_ai=1`      — AI panel closed, statements left to default.
    //   - none              — both panels boot to their normal initial state.
    //   - `?hide_max=1` — graph-only (hard):
    //                          suppress `<StatementFilterPanel>` entirely.
    //                          `AppBar` (with its Drawer), `PanelToggles` and
    //                          `GraphToolsBar` self-hide via
    //                          `isGraphOnlyMode()`. `<AIChat>` stays mounted
    //                          but boots closed (renders nothing while
    //                          closed) so in-graph AI actions can still pop
    //                          it open; add `hide_ai_actions=1` to suppress
    //                          AI entirely.
    const hideAlways = isHideAlways()
    const hideEdit = isHideEdit()
    const graphOnly = isGraphOnlyMode()
    // Independent embed-chrome toggles (Share dialog): each removes a piece
    // of chrome entirely, distinct from the soft open-state flags above.
    //   - `?hide_statement_controls=1` → no `<StatementFilterPanel>`.
    //   - `?hide_ai_actions=1`         → no `<AIChat>` (and the AI pill +
    //     in-graph AI row self-hide via the same flag).
    //   - `?hide_app_bar=1`            → no header `<AppBar>` (self-hides,
    //     URL-only — the AI-visible hero embed recipe).
    const hideStatements = isHideStatements()
    const hideAiActions = isHideAiActions()
    const [isStatementEditorOpen, setIsStatementEditorOpen] = useState(
        !hideAlways && !hideEdit && !graphOnly
    )

    // Graph loaded state now comes from the graph store (populated by bootstrap)
    const isGraphLoaded = useGraphStore(state => state.isGraphLoaded)
    const graphError = useGraphStore(state => state.error)
    const showSnackbar = useSnackbar(state => state.showSnackbar)
    const hasChatParam = Boolean(urlParams.get('chat'))
    const hasAiIdeaMode = urlParams.get('mode') === 'insight_ai_idea'
    const autoAddFromAiChat = hasChatParam || hasAiIdeaMode
    const addUserMessagesToGraph = hasChatParam

    const updatePanelOpen = useZustand(updatePanelOpenSelector)
    const setAutoAddFromAiChat = useZustand(setAutoAddFromAiChatSelector)
    const setAddUserMessagesToGraph = useZustand(
        setAddUserMessagesToGraphSelector
    )
    const isUpdatingFromStore = React.useRef(false)

    const determineViewType = () => {
        if (urlParams.get('missing')) return 'missing'
        return 'overview'
    }

    const [statementsViewType, setStatementsViewType] = useState(
        determineViewType()
    )

    const updateStatementsViewType = useZustand(
        updateStatementsViewTypeSelector
    )

    const setForceRequestMode = useZustand(setForceRequestModeSelector)

    useEffect(() => {
        updateStatementsViewType(statementsViewType)
    }, [statementsViewType])

    useEffect(() => {
        setAutoAddFromAiChat(autoAddFromAiChat)
    }, [autoAddFromAiChat, setAutoAddFromAiChat])

    useEffect(() => {
        setAddUserMessagesToGraph(addUserMessagesToGraph)
    }, [addUserMessagesToGraph, setAddUserMessagesToGraph])

    useEffect(() => {
        const { setWillAutoSend } = useZustand.getState()

        // Outside the AI-chat regime the host never seeds on load, so the
        // widget's insights view can fetch immediately (no settle wait).
        if (!autoAddFromAiChat) {
            if (useZustand.getState().willAutoSend !== false) {
                setWillAutoSend(false)
            }
            return
        }

        let didFire = false

        const evaluate = () => {
            const {
                statements,
                currentContext,
                loadedStatements,
                isAiChatReady,
                willAutoSend,
            } = useZustand.getState()
            // Statement count is unknown until the context's statements load —
            // leave `willAutoSend` as null so the widget keeps its settle
            // fallback for that brief window.
            if (!loadedStatements || !currentContext) return

            const ctxStatements = statements[currentContext] || []
            const willSeed =
                ctxStatements.length === 1 && !!ctxStatements[0].text

            // Publish the seed decision as soon as it's knowable (independent of
            // widget readiness) so the insights view can stand down / fetch
            // deterministically rather than racing a timer. Guard on a real
            // change — this runs inside a store subscription, and an
            // unconditional set() would re-trigger itself infinitely.
            if (willAutoSend !== willSeed) {
                setWillAutoSend(willSeed)
            }

            if (!willSeed || didFire) return
            // `isAiChatReady` is flipped from `<ChatWidget onReady>` once the
            // widget has hydrated (chats + context-settings fetched). Without
            // it, setting `forceRequestMode` here silently no-ops: the
            // widget's send action runs against an uninitialised session.
            if (!isAiChatReady) return

            didFire = true
            setForceRequestMode({
                name: 'gptchat',
                useInsightClusters: false,
                customInstruction: ctxStatements[0].text,
            })
        }

        evaluate()

        const unsubscribe = useZustand.subscribe(() => {
            evaluate()
        })

        return unsubscribe
    }, [autoAddFromAiChat, setForceRequestMode])

    // Assign useZustand to the global variable, so we can use it in the other parts of the app.
    // `usePanelStore` is also exposed so legacy EJS (LeftMenuLogic.ejs:generateShareURL)
    // can read the AI panel's `isAiChatOpen` to encode the right hide_* flag.
    React.useEffect(() => {
        window.zustand = useZustand
        window.usePanelStore = usePanelStore
        window.graphStore = useGraphStore
        // Test-only escape hatch for Cypress. The other globals above are
        // load-bearing for legacy EJS reads and stay ungated.
        if (process.env.NODE_ENV !== 'production') {
            window.contextsStore = useContextsStore
        }
    }, [])

    React.useEffect(() => {
        if (autoAddFromAiChat || hideAlways || hideEdit) {
            setIsStatementEditorOpen(false)
            const element = document.getElementById('statementmenu')
            if (element) {
                $('#statementmenu').removeClass('statementEditorOpen')
            }
            return
        }

        const folded = localStorage.getItem('folded')
        if (folded === '0') {
            const interval = setInterval(() => {
                const element = document.getElementById('statementmenu')
                if (element) {
                    setIsStatementEditorOpen(true)
                    $('#statementmenu').addClass('statementEditorOpen')
                    clearInterval(interval)
                }
            }, 300)
        } else if (folded == '1') {
            setIsStatementEditorOpen(false)
            const element = document.getElementById('statementmenu')
            if (element) {
                $('#statementmenu').removeClass('statementEditorOpen')
            }
        }
    }, [])

    // TODO MERGE: clean this up.
    React.useEffect(() => {
        const openImportDialog = ({
            dialogName,
            contextName,
            searchPhrase,
        }) => {
            setActiveImportDialog(dialogName)
            setPresetContext(contextName)
            setPresetSearchString(searchPhrase)
            setBackLinkDialog('backLink')
        }
        const onForceRequestMode = mode => {
            console.log('📢 Event received: ForceRequestMode', mode)
            setForceRequestMode(mode)
        }
        // Open the statements panel/editor on demand (e.g. the graph's "Select"
        // affordance selects clusters and asks to reveal their statements).
        const openStatementsPanel = () => {
            setIsStatementEditorOpen(true)
            const element = document.getElementById('statementmenu')
            if (element) {
                $('#statementmenu').addClass('statementEditorOpen')
            }
        }
        // NOTE: the legacy 'GraphLoaded'/'GraphUpdate'/'AnalyticsUpdate'
        // listeners were removed deliberately — they overwrote the
        // gap-derived insightClusters/allClusters (computed by setTopics)
        // with graphFactory's Sigma-era selection, desyncing the AI chat
        // from the graph's first gap. setTopics (useZustand) is the only
        // writer of those fields now. See docs/legacy-sigma-removal-plan.md.
        if (window.myEventEmitter) {
            myEventEmitter.on('openImportDialog', openImportDialog)
            myEventEmitter.on('ForceRequestMode', onForceRequestMode)
            myEventEmitter.on('openStatementsPanel', openStatementsPanel)
        }
        return () => {
            if (window.myEventEmitter) {
                myEventEmitter.removeListener(
                    'openImportDialog',
                    openImportDialog
                )
                myEventEmitter.removeListener(
                    'ForceRequestMode',
                    onForceRequestMode
                )
                myEventEmitter.removeListener(
                    'openStatementsPanel',
                    openStatementsPanel
                )
            }
        }
    }, [])

    React.useEffect(() => {
        const unsubscribe = useZustand.subscribe(
            state => state.panelOpen,
            panelOpen => {
                if (!isUpdatingFromStore.current) {
                    setIsStatementEditorOpen(panelOpen)
                }
            }
        )

        return unsubscribe
    }, [])

    React.useEffect(() => {
        if (!isGraphLoaded) return
        isUpdatingFromStore.current = true
        updatePanelOpen(isStatementEditorOpen)
        isUpdatingFromStore.current = false
    }, [isStatementEditorOpen, isGraphLoaded])

    const open = Boolean(activeImportDialog)

    const isOnGraphPage =
        location === 'context' ||
        location == 'conceptsSynthesis' ||
        location == 'synthesis' ||
        location == 'search'

    const isSearchView = location === 'search'
    const searchPhase = useFindStore(s => s.searchPhase)
    const isViewReady = isSearchView
        ? searchPhase === SEARCH_PHASES.LOADED
        : true
    const showFilterPanels = isOnGraphPage && isGraphLoaded && isViewReady
    const isSearchGraphReady = isSearchView && showFilterPanels

    const isLockedGraph = graphError === 'QUOTA_GRAPH_LOCKED'
    React.useEffect(() => {
        if (isOnGraphPage && graphError && !isGraphLoaded && !isLockedGraph) {
            showSnackbar(
                'error',
                `Error while loading graph: ${graphError}.\n Please contact support.`,
                'top',
                5000
            )
        }
    }, [graphError, isOnGraphPage, isGraphLoaded, isLockedGraph])

    return (
        <>
            <HelmetCode />
            {isOnGraphPage && isLockedGraph && <LockedGraphScreen />}
            <AppBar variant="dense" onToggle={setIsAppBarOpen} />
            <AccessGate>
                <FilterEventBridge />
                <FirstEntryController />
                <LanguageWarnings />
                <ImportDialog
                    open={open}
                    type={activeImportDialog}
                    onClose={closeImportDialog}
                    presetSearchString={presetSearchString}
                    presetContext={presetContext}
                    presetFiles={presetFiles}
                />
                <GraphMenu />
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        p: 3,
                        width: '100%',
                        position: 'relative',
                    }}
                >
                    {showFilterPanels && !graphOnly && !hideStatements && (
                        <StatementFilterPanel
                            isAppBarOpen={isAppBarOpen}
                            isStatementEditorOpen={isStatementEditorOpen}
                            setIsStatementEditorOpen={setIsStatementEditorOpen}
                        />
                    )}
                    {showFilterPanels && !hideAiActions && <AIChat />}
                    {isOnGraphPage && <PanelToggles />}
                    {isSearchView ? (
                        isSearchGraphReady ? (
                            <LazyGraph />
                        ) : (
                            <SearchEmptyStates />
                        )
                    ) : (
                        showFilterPanels && <LazyGraph />
                    )}
                </Box>
                <Outlet />
            </AccessGate>
        </>
    )
}
